import { useState } from 'react';
import axios from 'axios';
import RequestReset from './RequestReset';
import Modal from './Modal';
import { Link } from 'react-router-dom';
export default function Login({ onLogin }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showReset, setShowReset] = useState(false);
  const [success, setSuccess] = useState('');
  const isLocalMockAuth =
    import.meta.env.DEV ||
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1';

  const createDemoUser = () => ({
    data: {
      success: true,
      username: 'demo',
      userId: 'local-demo',
      email: 'demo@local.test',
      verified: true,
      timezone: 'America/Denver',
      localMock: true,
      // Only mark the demo session as admin in development or local environments.
      is_admin: import.meta.env.DEV ? 1 : 0,
    },
  });

  const showError = (message) => {
    setSuccess('');
    setError(message);
    setTimeout(() => setError(''), 5000);
  };

  const showSuccess = (message) => {
    setError('');
    setSuccess(message);
    setTimeout(() => setSuccess(''), 3000);
  };

  const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  const loginWithDemoAccount = async () => {
    setIsLoading(true);

    try {
      showSuccess('Using local demo account...');
      await delay(500);

      if (onLogin) {
        onLogin(createDemoUser());
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!username || !password) {
      showError('Please fill in all fields');
      return;
    }

    if (password.length < 6) {
      showError('Password must be at least 6 characters long');
      return;
    }

    setIsLoading(true);

    try {
      if (isLocalMockAuth && (username.toLowerCase() === 'demo' || password === 'demo123')) {
        await loginWithDemoAccount();
        return;
      }

      // Make API call to your backend
      const res = await axios.post('/api/login', {
        username: username, // or just use email directly if that's the field name
        password: password,
      });

      const data = res.data;

      if (data.success && data.username) {
        showSuccess('Login successful! Redirecting...');
        await delay(1000); // ⏳ Wait 1 second before continuing

        if (onLogin) {
          onLogin({
            data,
          });
        }
      }
    } catch (err) {
      console.error('Login error:', err);
      showError(
        err?.response?.data?.message || 'Login failed. Try the demo account or check the backend.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSubmit(e);
    }
  };

  return (
    <div className="auth-page">
      {showReset && (
        <Modal onClose={() => setShowReset(false)}>
          <RequestReset initialUsername={username} onClose={() => setShowReset(false)} />
        </Modal>
      )}

      <div className="auth-layout">
        <aside className="auth-hero">
          <div>
            <p className="auth-hero__eyebrow">Task board</p>
            <h1 className="auth-hero__title">TodoApp</h1>
            <p className="auth-hero__copy">
              Keep recurring work, daily digests, and your settings in one place with a layout that
              matches the rest of the app.
            </p>
          </div>

          <div className="auth-hero__notes">
            <span className="auth-hero__note">Recurring tasks</span>
            <span className="auth-hero__note">Daily reports</span>
            <span className="auth-hero__note">Theme + timezone aware</span>
          </div>
        </aside>

        <section className="auth-card" style={{ animation: 'slideUp 0.6s ease-out' }}>
          <h2 className="auth-card__title">Welcome back!</h2>
          <p className="auth-card__copy">
            Sign in to your workspace and continue where you left off.
          </p>

          {error && (
            <div className="settings-status" style={{ color: '#b42318' }}>
              {error}
            </div>
          )}

          {success && (
            <div className="settings-status" style={{ color: 'var(--brand)' }}>
              {success}
            </div>
          )}

          <form onSubmit={handleSubmit} className="auth-form">
            <div className="auth-field">
              <label className="auth-label">Username</label>
              <input
                className="auth-input"
                type="text"
                name="username"
                autoComplete="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Enter your username"
              />
            </div>

            <div className="auth-field">
              <label className="auth-label">Password</label>
              <div className="auth-input-wrap">
                <input
                  className="auth-input"
                  type={isPasswordVisible ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                  onClick={() => setIsPasswordVisible((v) => !v)}
                  className="auth-toggle"
                >
                  {isPasswordVisible ? '🙈' : '👁️'}
                </button>
              </div>
            </div>

            <div className="auth-actions">
              <button type="submit" disabled={isLoading} className="auth-submit">
                {isLoading ? 'Signing in...' : 'Sign in'}
              </button>

              {isLocalMockAuth && (
                <>
                  <button
                    type="button"
                    onClick={loginWithDemoAccount}
                    disabled={isLoading}
                    className="auth-secondary"
                  >
                    Use Local Demo Account
                  </button>
                  <span
                    style={{
                      color: 'var(--panel-muted)',
                      fontSize: '0.92rem',
                      textAlign: 'center',
                    }}
                  >
                    Demo login: any username with password `demo123`, or just click the button.
                  </span>
                </>
              )}
            </div>

            <div className="auth-divider">or</div>

            <div className="auth-footer">
              <button type="button" onClick={() => setShowReset(true)} className="auth-secondary">
                Forgot Password?
              </button>

              <div className="text-center text-sm" style={{ color: 'var(--panel-muted)' }}>
                Don&apos;t have an account?{' '}
                <Link to="/register" className="auth-link">
                  Sign up here
                </Link>
              </div>
            </div>
          </form>
        </section>
      </div>

      <style>{`
      @keyframes slideUp {
        from {
          opacity: 0;
          transform: translateY(30px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `}</style>
    </div>
  );
}
